import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CATEGORIES, MALAYSIA_STATES } from '../utils/constants';
import { isValidMalaysianPhone } from '../utils/validation';
import { Header } from '../components/Header';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Select } from '../components/ui/Select';
import { Label } from '../components/ui/Label';
import { PhotoUpload } from '../components/PhotoUpload';
interface FormData {
  category: string;
  issue: string;
  name: string;
  phone: string;
  state: string;
  postcode: string;
  address: string;
  photos: File[];
  appointmentDate: string;
  timeSlot: string;
}
interface FormErrors {
  category?: string;
  issue?: string;
  name?: string;
  phone?: string;
  state?: string;
  postcode?: string;
  address?: string;
  appointmentDate?: string;
  timeSlot?: string;
}
export function RequestForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || '';
  const [formData, setFormData] = useState<FormData>({
    category: initialCategory,
    issue: '',
    name: '',
    phone: '',
    state: 'kl',
    postcode: '',
    address: '',
    photos: [],
    appointmentDate: '',
    timeSlot: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Update category if URL param changes
  useEffect(() => {
    if (initialCategory) {
      setFormData(prev => ({
        ...prev,
        category: initialCategory
      }));
    }
  }, [initialCategory]);
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;
    if (!formData.category) {
      newErrors.category = 'Please select a service category';
      isValid = false;
    }
    if (!formData.issue || formData.issue.length < 10) {
      newErrors.issue = 'Please describe the issue (min 10 characters)';
      isValid = false;
    }
    if (!formData.appointmentDate) {
      newErrors.appointmentDate = 'Please select a preferred date';
      isValid = false;
    }
    if (!formData.timeSlot) {
      newErrors.timeSlot = 'Please select a time slot';
      isValid = false;
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
      isValid = false;
    } else if (!isValidMalaysianPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid Malaysian phone number (e.g. 0123456789)';
      isValid = false;
    }
    if (!formData.state) {
      newErrors.state = 'State is required';
      isValid = false;
    }
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
      isValid = false;
    } else if (!/^\d{5}$/.test(formData.postcode.trim())) {
      newErrors.postcode = 'Please enter a valid 5-digit postcode';
      isValid = false;
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      // Scroll to top error
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/success', {
        state: {
          categoryName: CATEGORIES.find(c => c.id === formData.category)?.name || 'Service',
          requestId: Date.now().toString()
        }
      });
    }, 1500);
  };
  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user types
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const categoryOptions = CATEGORIES.map(c => ({
    value: c.id,
    label: c.name
  }));
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-149">
      <Header data-id="element-150" />

      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-8" data-id="element-151">
        <button onClick={() => navigate('/')} className="flex items-center text-gray-500 hover:text-teal-600 mb-6 transition-colors text-sm font-medium" data-id="element-152">
          <ArrowLeft className="w-4 h-4 mr-1" data-id="element-153" />
          Back to Categories
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-154">
          <div className="bg-teal-600 px-6 py-4" data-id="element-155">
            <h2 className="text-xl font-bold text-white" data-id="element-156">Request Service</h2>
            <p className="text-teal-100 text-sm" data-id="element-157">
              Fill in the details below to get a quote.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-158">
            {/* Service Category */}
            <div data-id="element-159">
              <Label htmlFor="category" required data-id="element-160">
                Service Category
              </Label>
              <Select id="category" options={categoryOptions} value={formData.category} onChange={e => handleChange('category', e.target.value)} error={errors.category} placeholder="Select a category" data-id="element-161" />
            </div>

            {/* Issue Description */}
            <div data-id="element-162">
              <Label htmlFor="issue" required data-id="element-163">
                Describe Issue
              </Label>
              <Textarea id="issue" rows={4} placeholder="Tell us about your issue... (e.g. Aircon is not cooling, leaking water)" value={formData.issue} onChange={e => handleChange('issue', e.target.value)} error={errors.issue} data-id="element-164" />
            </div>

            {/* Photos */}
            <div data-id="element-165">
              <PhotoUpload files={formData.photos} onChange={files => handleChange('photos', files)} data-id="element-166" />
            </div>

            {/* Schedule Appointment */}
            <div className="border-t border-gray-100 pt-6" data-id="element-167">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-168">
                Schedule Appointment
              </h3>
              <div className="space-y-4" data-id="element-169">
                <div data-id="element-170">
                  <Label htmlFor="appointmentDate" required data-id="element-171">
                    Preferred Date
                  </Label>
                  <Input id="appointmentDate" type="date" min={new Date().toISOString().split('T')[0]} value={formData.appointmentDate} onChange={e => handleChange('appointmentDate', e.target.value)} error={errors.appointmentDate} data-id="element-172" />
                </div>

                <div data-id="element-173">
                  <Label required data-id="element-174">Preferred Time Slot</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1.5" data-id="element-175">
                    {[{
                    id: 'morning',
                    label: 'Morning',
                    time: '9AM - 12PM'
                  }, {
                    id: 'afternoon',
                    label: 'Afternoon',
                    time: '12PM - 3PM'
                  }, {
                    id: 'evening',
                    label: 'Evening',
                    time: '3PM - 6PM'
                  }].map(slot => <button key={slot.id} type="button" onClick={() => handleChange('timeSlot', slot.id)} className={`
                          relative flex flex-col items-center justify-center p-3 rounded-lg border transition-all duration-200
                          ${formData.timeSlot === slot.id ? 'bg-teal-600 border-teal-600 text-white shadow-md ring-2 ring-teal-600 ring-offset-1' : 'bg-white border-gray-200 text-gray-700 hover:border-teal-500 hover:text-teal-600 hover:bg-teal-50'}
                        `} data-id="element-176">
                        <span className="font-semibold text-sm" data-id="element-177">
                          {slot.label}
                        </span>
                        <span className={`text-xs mt-1 ${formData.timeSlot === slot.id ? 'text-teal-100' : 'text-gray-500'}`} data-id="element-178">
                          {slot.time}
                        </span>
                      </button>)}
                  </div>
                  {errors.timeSlot && <p className="mt-1.5 text-sm text-red-500" data-id="element-179">
                      {errors.timeSlot}
                    </p>}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6" data-id="element-180">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-181">
                Contact Details
              </h3>

              <div className="space-y-4" data-id="element-182">
                {/* Name */}
                <div data-id="element-183">
                  <Label htmlFor="name" required data-id="element-184">
                    Your Name
                  </Label>
                  <Input id="name" type="text" placeholder="John Doe" value={formData.name} onChange={e => handleChange('name', e.target.value)} error={errors.name} data-id="element-185" />
                </div>

                {/* Phone */}
                <div data-id="element-186">
                  <Label htmlFor="phone" required data-id="element-187">
                    Contact Number
                  </Label>
                  <Input id="phone" type="tel" placeholder="0123456789" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} error={errors.phone} data-id="element-188" />
                </div>

                {/* State */}
                <div data-id="element-189">
                  <Label htmlFor="state" required data-id="element-190">
                    State
                  </Label>
                  <Select id="state" options={MALAYSIA_STATES} value={formData.state} onChange={e => handleChange('state', e.target.value)} error={errors.state} data-id="element-191" />
                </div>

                {/* Postcode */}
                <div data-id="element-192">
                  <Label htmlFor="postcode" required data-id="element-193">
                    Postcode
                  </Label>
                  <Input id="postcode" type="text" placeholder="50000" maxLength={5} value={formData.postcode} onChange={e => handleChange('postcode', e.target.value)} error={errors.postcode} data-id="element-194" />
                </div>

                {/* Address */}
                <div data-id="element-195">
                  <Label htmlFor="address" required data-id="element-196">
                    Address / Area
                  </Label>
                  <Input id="address" type="text" placeholder="e.g. Mont Kiara, Condo Name, Unit #" value={formData.address} onChange={e => handleChange('address', e.target.value)} error={errors.address} data-id="element-197" />
                </div>
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row gap-3" data-id="element-198">
              <Button type="submit" fullWidth isLoading={isSubmitting} className="order-1 sm:order-2" data-id="element-199">
                Submit Request
              </Button>
              <Button type="button" variant="secondary" fullWidth onClick={() => navigate('/')} className="order-2 sm:order-1" disabled={isSubmitting} data-id="element-200">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>;
}